/**
 * CompletionCelebration — web port of
 * client-native/src/features/tutorial/components/CompletionCelebration.tsx.
 *
 * Full-screen overlay shown when the tutorial hits 100%. A parrot swoops in
 * from the bottom-left over a white smokey diagonal vignette, then the copy
 * "Slaayyyy!" / "You got the basics down" fades in. Auto-dismisses after 4s
 * (or on click). Uses the iOS flying.png asset (280×280) plus 8 colored flying
 * streaks — same motion + copy. Centered within a max-width column.
 */
import { FC, useEffect, useMemo, useState } from "react";
import { colors } from "@/ui";
import flyingImage from "../assets/flying.png";

interface CompletionCelebrationProps {
  isVisible: boolean;
  onDismiss?: () => void;
}

const FLY_MS = 600;
const AUTO_DISMISS_MS = 4000;

// Flying streak colors (mirror iOS STREAK_COLORS).
const STREAK_COLORS = [
  "rgba(255, 215, 0, 0.6)", // Gold
  "rgba(255, 182, 193, 0.5)", // Light pink
  "rgba(135, 206, 250, 0.5)", // Light sky blue
  "rgba(144, 238, 144, 0.5)", // Light green
  "rgba(255, 218, 185, 0.5)", // Peach
];

interface StreakConfig {
  id: number;
  delayMs: number;
  durationMs: number;
  startXvw: number;
  startYvh: number;
  travelXvw: number;
  travelYvh: number;
  length: number;
  color: string;
  rotation: number;
}

/** 8 streaks from the bottom-left quadrant, mirroring iOS generateStreaks(). */
const generateStreaks = (): StreakConfig[] =>
  Array.from({ length: 8 }, (_, i) => ({
    id: i,
    delayMs: i * 80 + Math.random() * 150,
    durationMs: 400 + Math.random() * 500,
    startXvw: -18 + Math.random() * 50,
    startYvh: 60 + Math.random() * 35,
    travelXvw: 50 + Math.random() * 50,
    travelYvh: -(30 + Math.random() * 40),
    length: 50 + Math.random() * 100,
    color: STREAK_COLORS[i % STREAK_COLORS.length],
    rotation: -25 - Math.random() * 25,
  }));

const CompletionCelebration: FC<CompletionCelebrationProps> = ({
  isVisible,
  onDismiss,
}) => {
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const streaks = useMemo(() => generateStreaks(), []);

  useEffect(() => {
    if (!isVisible) return;
    setMounted(true);
    const raf = requestAnimationFrame(() => setEntered(true));
    const dismissTimer = window.setTimeout(() => {
      setEntered(false);
      window.setTimeout(() => {
        setMounted(false);
        onDismiss?.();
      }, 400);
    }, AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(dismissTimer);
    };
  }, [isVisible, onDismiss]);

  if (!mounted) return null;

  const handleDismiss = () => {
    setEntered(false);
    window.setTimeout(() => {
      setMounted(false);
      onDismiss?.();
    }, 300);
  };

  // Layered diagonal "smokey" bands (outer faint → inner bright), -35deg.
  const bands = [
    { h: "90%", c: "rgba(255,254,254,0.08)" },
    { h: "80%", c: "rgba(255,255,255,0.27)" },
    { h: "70%", c: "rgba(255,255,255,0.4)" },
    { h: "60%", c: "rgba(255,255,255,0.58)" },
    { h: "50%", c: "rgba(255,255,255,0.72)" },
    { h: "40%", c: "rgba(255,255,255,0.86)" },
  ];

  return (
    <div
      onClick={handleDismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        opacity: entered ? 1 : 0,
        transition: "opacity 400ms ease",
        cursor: "pointer",
      }}
    >
      {/* Diagonal vignette bands */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
        aria-hidden
      >
        {bands.map((b, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: "180%",
              height: b.h,
              backgroundColor: b.c,
              transform: "rotate(-35deg)",
            }}
          />
        ))}
      </div>

      {/* Flying streaks */}
      <div style={{ position: "absolute", inset: 0, overflow: "hidden" }} aria-hidden>
        <style>{streaks
          .map(
            (s) => `
          @keyframes streak-${s.id} {
            0% { transform: translate(${s.startXvw}vw, ${s.startYvh}vh) rotate(${s.rotation}deg); opacity: 0; }
            15% { opacity: 0.7; }
            60% { opacity: 0.5; }
            100% { transform: translate(${s.startXvw + s.travelXvw}vw, ${s.startYvh + s.travelYvh}vh) rotate(${s.rotation}deg); opacity: 0; }
          }`
          )
          .join("\n")}</style>
        {entered &&
          streaks.map((s) => (
            <div
              key={s.id}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: s.length,
                height: 4,
                borderRadius: 2,
                backgroundColor: s.color,
                boxShadow: "-20px 0 10px rgba(255,215,0,0.5)",
                animation: `streak-${s.id} ${s.durationMs}ms ${s.delayMs}ms ease-out forwards`,
              }}
            />
          ))}
      </div>

      {/* Parrot + copy */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: 40,
        }}
      >
        <img
          src={flyingImage}
          alt=""
          aria-hidden
          style={{
            width: 280,
            height: 280,
            objectFit: "contain",
            transform: entered
              ? "translate(0px, -40px) scale(1)"
              : "translate(-40vw, 30vh) scale(0.3)",
            transition: `transform ${FLY_MS}ms cubic-bezier(0.33,1,0.68,1)`,
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            opacity: entered ? 1 : 0,
            transition: `opacity 300ms ease ${FLY_MS}ms`,
          }}
        >
          <span
            style={{
              fontSize: "var(--base-font-size-3xlarge)",
              fontWeight: 700,
              color: colors.text.primary,
            }}
          >
            Slaayyyy!
          </span>
          <span
            style={{
              fontSize: "var(--base-font-size-large)",
              color: colors.muted,
              marginTop: 4,
            }}
          >
            You got the basics down
          </span>
        </div>
      </div>
    </div>
  );
};

export default CompletionCelebration;
